/**
 * Per-browser preferences for the Dok Pabean list (next-jarvis#961): which
 * columns show, whether company logos and country flags render, and when the
 * "Baru" chip on the Kolom button stops showing. Pure logic only, so it can be
 * unit-tested without React.
 */

/** Page keys: each page that renders the doc-pabean table keeps its own choices. */
export type DocPabeanTablePageKey = 'doc-pabean-table' | 'monitoring-blokir-table';

export type DocPabeanColumnMeta = {
  accessor: string;
  titleKey: string;
  /** False = always shown, never listed in the Kolom picker. */
  toggleable: boolean;
};

/**
 * Every column of the doc-pabean table, in display order. The table and the
 * Kolom picker both derive from this list, so the two `useDataTableColumns`
 * instances that share a localStorage key always see the same accessors and
 * never "self-heal" each other's stored toggles.
 */
export const DOC_PABEAN_COLUMNS: readonly DocPabeanColumnMeta[] = [
  { accessor: 'nomorAju', titleKey: 'docpabean.colNomorAju', toggleable: false },
  { accessor: 'shipmentType', titleKey: 'docpabean.colType', toggleable: true },
  { accessor: 'namaPerusahaan', titleKey: 'docpabean.colShipper', toggleable: true },
  { accessor: 'buyerName', titleKey: 'docpabean.colBuyer', toggleable: true },
  { accessor: 'vesselVoy', titleKey: 'docpabean.colVesselVoy', toggleable: true },
  { accessor: 'tanggalTibaEkspor', titleKey: 'docpabean.colTglTibaEkspor', toggleable: true },
  { accessor: 'tanggalDaftar', titleKey: 'docpabean.colDate', toggleable: true },
  { accessor: 'respon', titleKey: 'docpabean.colRespon', toggleable: true },
  { accessor: 'status', titleKey: 'docpabean.colStatus', toggleable: true },
  { accessor: 'jenisKategori', titleKey: 'docpabean.colJenisKategori', toggleable: true },
  { accessor: 'drafter', titleKey: 'docpabean.colDrafter', toggleable: true },
  { accessor: 'totalItem', titleKey: 'docpabean.colTotalItem', toggleable: true },
  { accessor: 'approvalStatus', titleKey: 'docpabean.colApproval', toggleable: true },
  { accessor: 'actions', titleKey: 'docpabean.colActions', toggleable: false },
];

type ToggleLike = { accessor: string; toggled: boolean };

/**
 * Whether a column is hidden. Non-toggleable columns (Nomor Aju, Actions) are
 * always shown whatever storage says; an accessor missing from storage shows.
 */
export function isColumnHidden(accessor: string, toggles: readonly ToggleLike[] | null | undefined): boolean {
  const meta = DOC_PABEAN_COLUMNS.find((c) => c.accessor === accessor);
  if (!meta || !meta.toggleable) return false;
  const stored = Array.isArray(toggles) ? toggles.find((t) => t?.accessor === accessor) : undefined;
  return stored ? stored.toggled === false : false;
}

export type DocPabeanDisplayPrefs = { logos: boolean; flags: boolean };

export const DEFAULT_DISPLAY_PREFS: DocPabeanDisplayPrefs = { logos: true, flags: true };

export const displayPrefsKey = (pageKey: DocPabeanTablePageKey) => `doc-pabean-display:${pageKey}`;

export const KOLOM_SEEN_KEY = 'doc-pabean-kolom-seen';

/** Anything malformed in storage falls back to the default for that switch. */
export function normalizeDisplayPrefs(value: unknown): DocPabeanDisplayPrefs {
  if (!value || typeof value !== 'object') return { ...DEFAULT_DISPLAY_PREFS };
  const v = value as Partial<Record<keyof DocPabeanDisplayPrefs, unknown>>;
  return {
    logos: typeof v.logos === 'boolean' ? v.logos : DEFAULT_DISPLAY_PREFS.logos,
    flags: typeof v.flags === 'boolean' ? v.flags : DEFAULT_DISPLAY_PREFS.flags,
  };
}

/**
 * True while `now` is on or before the end of `until` (YYYY-MM-DD) in WIB
 * (UTC+7, no DST). A malformed `until` never shows the chip.
 */
export function isKolomPickerNew(now: Date, until: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(until)) return false;
  const endOfDayWib = Date.parse(`${until}T23:59:59.999+07:00`);
  if (Number.isNaN(endOfDayWib)) return false;
  return now.getTime() <= endOfDayWib;
}

/** Trimmed string, or null when blank/absent. */
export function cleanText(value: string | null | undefined): string | null {
  const s = typeof value === 'string' ? value.trim() : '';
  return s ? s : null;
}

/** The mobile card's "Vessel: … · ETA/ETD: …" pieces; null when there is nothing to show. */
export function vesselDateSummary(row: {
  namaPengangkut?: string | null;
  nomorPengangkut?: string | null;
  tanggalTibaEkspor?: string | null;
  tanggalTibaEksporRaw?: string | null;
}): { vessel: string | null; date: string | null } | null {
  const name = cleanText(row.namaPengangkut);
  const voy = cleanText(row.nomorPengangkut);
  const vessel = name && voy ? `${name} / ${voy}` : name || voy;
  const date = cleanText(row.tanggalTibaEkspor) || cleanText(row.tanggalTibaEksporRaw);
  if (!vessel && !date) return null;
  return { vessel, date };
}
